import type { UpskillingProgramTemplate } from '@/domains/pages/ai-proficiency/fixtures/upskilling-program-templates';
import { AIFI_FONT_FAMILY } from '@/domains/pages/ai-proficiency/typography';

const FONT_FAMILY = `${AIFI_FONT_FAMILY}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;

type ProgramEmailTheme = {
  bg: string;
  text: string;
  textMuted: string;
  border: string;
  objectiveBg: string;
  objectiveLabel: string;
  objectiveText: string;
  intro: string;
  exampleLabel: string;
  insteadBg: string;
  insteadBorder: string;
  insteadLabel: string;
  insteadText: string;
  tryBg: string;
  tryBorder: string;
  tryLabel: string;
  tryText: string;
  checklistBg: string;
  checklistBorder: string;
  checklistTitle: string;
  checklistText: string;
  checklistCheck: string;
};

const LIGHT_THEME: ProgramEmailTheme = {
  bg: '#FFFFFF',
  text: '#111827',
  textMuted: '#9CA3AF',
  border: '#E5E7EB',
  objectiveBg: '#E8F0FE',
  objectiveLabel: '#1A73E8',
  objectiveText: '#174EA6',
  intro: '#374151',
  exampleLabel: '#6B7280',
  insteadBg: '#FCE8E6',
  insteadBorder: '#D93025',
  insteadLabel: '#D93025',
  insteadText: '#A50E0E',
  tryBg: '#E6F4EA',
  tryBorder: '#1E8E3E',
  tryLabel: '#1E8E3E',
  tryText: '#0D652D',
  checklistBg: '#F9FAFB',
  checklistBorder: '#E5E7EB',
  checklistTitle: '#111827',
  checklistText: '#111827',
  checklistCheck: '#1E8E3E',
};

const DARK_THEME: ProgramEmailTheme = {
  bg: '#18181B',
  text: '#FAFAFA',
  textMuted: '#A1A1AA',
  border: '#27272A',
  objectiveBg: '#1E293B',
  objectiveLabel: '#60A5FA',
  objectiveText: '#93C5FD',
  intro: '#D4D4D8',
  exampleLabel: '#A1A1AA',
  insteadBg: '#3F1D1D',
  insteadBorder: '#EF4444',
  insteadLabel: '#F87171',
  insteadText: '#FECACA',
  tryBg: '#14532D',
  tryBorder: '#22C55E',
  tryLabel: '#4ADE80',
  tryText: '#BBF7D0',
  checklistBg: '#27272A',
  checklistBorder: '#3F3F46',
  checklistTitle: '#FAFAFA',
  checklistText: '#E4E4E7',
  checklistCheck: '#4ADE80',
};

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function buildInsteadOfBox(insteadOf: string, theme: ProgramEmailTheme, className = ''): string {
  return `
    <div class="nebuly-instead${className ? ` ${className}` : ''}" style="margin:0 0 8px;padding:12px 14px;background-color:${theme.insteadBg};border-left:4px solid ${theme.insteadBorder};border-radius:8px;">
      <div class="nebuly-instead-label" style="margin:0 0 6px;font-family:${FONT_FAMILY};font-size:12px;font-weight:600;color:${theme.insteadLabel};">Instead of</div>
      <div class="nebuly-instead-text" style="margin:0;font-family:${FONT_FAMILY};font-size:13px;line-height:1.5;color:${theme.insteadText};">${escapeHtml(insteadOf)}</div>
    </div>
  `;
}

function buildTryBox(tryText: string, theme: ProgramEmailTheme, className = ''): string {
  return `
    <div class="nebuly-try${className ? ` ${className}` : ''}" style="margin:0 0 20px;padding:12px 14px;background-color:${theme.tryBg};border-left:4px solid ${theme.tryBorder};border-radius:8px;">
      <div class="nebuly-try-label" style="margin:0 0 6px;font-family:${FONT_FAMILY};font-size:12px;font-weight:600;color:${theme.tryLabel};">Try</div>
      <div class="nebuly-try-text" style="margin:0;font-family:${FONT_FAMILY};font-size:13px;line-height:1.5;color:${theme.tryText};">${escapeHtml(tryText)}</div>
    </div>
  `;
}

function buildExample(
  example: UpskillingProgramTemplate['examples'][number],
  theme: ProgramEmailTheme,
): string {
  return `
    <div class="nebuly-example-label" style="margin:0 0 8px;font-family:${FONT_FAMILY};font-size:12px;color:${theme.exampleLabel};">${escapeHtml(example.label)}</div>
    ${buildInsteadOfBox(example.insteadOf, theme)}
    ${buildTryBox(example.tryText, theme)}
  `;
}

function buildChecklist(items: string[], theme: ProgramEmailTheme): string {
  const rows = items
    .map(
      (item) => `
        <tr>
          <td class="nebuly-checklist-check" style="width:20px;padding:0 8px 8px 0;vertical-align:top;font-family:${FONT_FAMILY};font-size:14px;color:${theme.checklistCheck};">&#10003;</td>
          <td class="nebuly-checklist-item" style="padding:0 0 8px;vertical-align:top;font-family:${FONT_FAMILY};font-size:14px;line-height:1.5;color:${theme.checklistText};">${escapeHtml(item)}</td>
        </tr>
      `,
    )
    .join('');

  return `
    <div class="nebuly-checklist" style="margin-top:4px;padding:16px 18px;background-color:${theme.checklistBg};border:1px solid ${theme.checklistBorder};border-radius:10px;">
      <div class="nebuly-checklist-title" style="margin:0 0 12px;font-family:${FONT_FAMILY};font-size:15px;font-weight:700;color:${theme.checklistTitle};">Before you hit enter, check</div>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">${rows}</table>
    </div>
  `;
}

function buildProgramEmailBody(template: UpskillingProgramTemplate, theme: ProgramEmailTheme): string {
  const examplesHtml = template.examples.map((example) => buildExample(example, theme)).join('');

  return `
<div class="nebuly-email" style="max-width:640px;margin:0 auto;padding:24px;background-color:${theme.bg};border-radius:12px;font-family:${FONT_FAMILY};color:${theme.text};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-bottom:20px;">
    <tr>
      <td class="nebuly-email-title nebuly-email-header" style="padding:0 0 12px;font-size:16px;font-weight:700;color:${theme.text};border-bottom:1px solid ${theme.border};">One habit to try this week</td>
      <td class="nebuly-email-byline nebuly-email-header" style="padding:0 0 12px;font-size:12px;color:${theme.textMuted};text-align:right;border-bottom:1px solid ${theme.border};">from Nebuly</td>
    </tr>
  </table>

  <div class="nebuly-objective" style="margin:0 0 20px;padding:16px 18px;background-color:${theme.objectiveBg};border-radius:10px;">
    <div class="nebuly-objective-label" style="margin:0 0 8px;font-size:12px;font-weight:600;color:${theme.objectiveLabel};">Your objective</div>
    <div class="nebuly-objective-text" style="margin:0;font-size:18px;line-height:1.35;font-weight:700;color:${theme.objectiveText};">${escapeHtml(template.objective)}</div>
  </div>

  <p class="nebuly-intro" style="margin:0 0 24px;font-size:14px;line-height:1.6;color:${theme.intro};">${escapeHtml(template.intro)}</p>

  ${examplesHtml}

  ${buildChecklist(template.checklist, theme)}
</div>
  `.trim();
}

function buildDarkModeStyleBlock(): string {
  const t = DARK_THEME;

  return `
<style type="text/css">
  :root { color-scheme: light dark; supported-color-schemes: light dark; }
  @media (prefers-color-scheme: dark) {
    .nebuly-email { background-color: ${t.bg} !important; color: ${t.text} !important; }
    .nebuly-email-title { color: ${t.text} !important; }
    .nebuly-email-byline { color: ${t.textMuted} !important; }
    .nebuly-email-header { border-bottom-color: ${t.border} !important; }
    .nebuly-objective { background-color: ${t.objectiveBg} !important; }
    .nebuly-objective-label { color: ${t.objectiveLabel} !important; }
    .nebuly-objective-text { color: ${t.objectiveText} !important; }
    .nebuly-intro { color: ${t.intro} !important; }
    .nebuly-example-label { color: ${t.exampleLabel} !important; }
    .nebuly-instead { background-color: ${t.insteadBg} !important; border-left-color: ${t.insteadBorder} !important; }
    .nebuly-instead-label { color: ${t.insteadLabel} !important; }
    .nebuly-instead-text { color: ${t.insteadText} !important; }
    .nebuly-try { background-color: ${t.tryBg} !important; border-left-color: ${t.tryBorder} !important; }
    .nebuly-try-label { color: ${t.tryLabel} !important; }
    .nebuly-try-text { color: ${t.tryText} !important; }
    .nebuly-checklist { background-color: ${t.checklistBg} !important; border-color: ${t.checklistBorder} !important; }
    .nebuly-checklist-title { color: ${t.checklistTitle} !important; }
    .nebuly-checklist-item { color: ${t.checklistText} !important; }
    .nebuly-checklist-check { color: ${t.checklistCheck} !important; }
  }
</style>
  `.trim();
}

/** Dark-themed HTML for the in-app email preview (platform is always dark). */
export function buildProgramEmailPreviewHtml(template: UpskillingProgramTemplate): string {
  return buildProgramEmailBody(template, DARK_THEME);
}

/** Email-safe HTML for copy/paste — light by default, dark via prefers-color-scheme. */
export function buildProgramEmailHtml(template: UpskillingProgramTemplate): string {
  return `${buildDarkModeStyleBlock()}${buildProgramEmailBody(template, LIGHT_THEME)}`;
}

export function buildProgramEmailPlainText(template: UpskillingProgramTemplate): string {
  const examples = template.examples
    .map(
      (example) =>
        `${example.label}\nInstead of: ${example.insteadOf}\nTry: ${example.tryText}`,
    )
    .join('\n\n');

  const checklist = template.checklist.map((item) => `- ${item}`).join('\n');

  return [
    'One habit to try this week',
    '',
    `Your objective: ${template.objective}`,
    '',
    template.intro,
    '',
    examples,
    '',
    'Before you hit enter, check',
    checklist,
  ].join('\n');
}
